import { useContext, useMemo } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import BasicFormWizardContext from 'hooks/contexts/BasicFormWizardContext';
import { QUESTION_IDS, FIELD_NAMES } from 'consts';
import { findValueInOptions } from 'utils/formValuesUtils';
import { LogDebug } from 'utils/logging';

const {
  SUBJECT_0F_INTEREST,
  FIRST_NAME,
  LAST_NAME,
  PRIMARY_PHONE,
  EMAIL,
  BEST_MATCHING_PROGRAM,
  ZIP,
} = QUESTION_IDS;

function getProgramValue(programGuid, programs) {
  if (!programGuid || !programs?.length) {
    return '';
  }
  const _program = programs.find((program) => program.guid === programGuid);
  return _program?.degreeValue || '';
}

export function replaceTokens(text, tokenMap = {}) {
  // filter out any tokens that are improperly formatted
  // and create a single string to regex against
  const tokenStringsForRegExt = Object.keys(tokenMap)
    .filter((token) => token.match(/^{\w+}$/gi))
    .join('|');
  if (!tokenStringsForRegExt) {
    return text;
  }

  const tokensRegEx = new RegExp(tokenStringsForRegExt, 'gi');

  return text.replace(tokensRegEx, (match) => {
    return tokenMap[match];
  });
}

/**
 * Replace tokens in a given text string with values.
 *
 * @param {string} textString - The text string to replace tokens in
 * @param {Object} tokens - An object with keys as token strings and values as the replacement values. Default is an empty object. Example: {{TOKEN_NAME}: 'token value'}
 *
 * @return {string} The tokenized text
 * */
export function tokenReplacement(textString, tokens = {}, tokenValues = {}) {
  const { phoneNumber, secondaryPhoneNumber } = tokenValues;
  const defaultTokenMap = {
    '{YEAR}': new Date().getFullYear(),
    '{PHONE}': phoneNumber
      ? `<span data-tf-element-role="consent-grantor-phone">${phoneNumber}</span>`
      : '',
    '{PHONE_SECONDARY}': secondaryPhoneNumber
      ? ` and/or ${secondaryPhoneNumber}`
      : '',
    '{PHONE_NUMBER}': phoneNumber || '',
  };

  // merge default tokens with any passed in
  const currentTokens = { ...defaultTokenMap, ...tokens };

  return replaceTokens(textString, currentTokens);
}

/**
 * Custom React hook to replace tokens in a given text string with values.
 *
 * @param {string} textString - The text string to replace tokens in
 * @param {Object} tokens - An object with keys as token strings and values as the replacement values. Default is an empty object. Example: {{TOKEN_NAME}: 'token value'}
 *
 * @return {string} The tokenized text
 * */

export default function useTokenReplacement(textString, additionalTokens = {}) {
  const {
    siteMeta,
    userData: { submittedUserFormValues },
    microSiteTaxonomyMap,
  } = useContext(GlobalContext);

  // Safely extract programs from microSiteTaxonomyMap
  const programs = microSiteTaxonomyMap?.programs || [];
  const { formValues, allQuestionsInForm, formStatus } = useContext(
    BasicFormWizardContext
  );

  const defaultTokenMap = useMemo(() => {
    // subject value is sometimes an object, sometimes a string
    const _selectedSubject =
      formValues[allQuestionsInForm?.[SUBJECT_0F_INTEREST]?.name];
    let _subjectLabel = _selectedSubject?.label;
    if (typeof _selectedSubject?.label === 'undefined') {
      // TODO: [T1-13519] we will have to check options not dynamic options as its server side fetched now
      const fishedSubject = findValueInOptions(
        _selectedSubject,
        formStatus.dynamicOptions?.[SUBJECT_0F_INTEREST]?.options || []
      );

      // SUPER EDGE CASE: session values came back but options call failed
      if (fishedSubject?.isMissing) {
        LogDebug('useTokenReplacement: subject label is missing');
        _subjectLabel = '';
      } else {
        _subjectLabel = fishedSubject?.label;
      }
    }
    const userValues =
      Object.keys(formValues).length > 0 ? formValues : submittedUserFormValues;
    const firstNameField =
      allQuestionsInForm?.[FIRST_NAME]?.name || FIELD_NAMES.FIRST_NAME;
    const lastNameField =
      allQuestionsInForm?.[LAST_NAME]?.name || FIELD_NAMES.LAST_NAME;
    const emailField = allQuestionsInForm?.[EMAIL]?.name || FIELD_NAMES.EMAIL;
    const bestMatchingProgramField =
      allQuestionsInForm?.[BEST_MATCHING_PROGRAM]?.name ||
      FIELD_NAMES.BEST_MATCHING_PROGRAM;
    const zipCodeField = allQuestionsInForm?.[ZIP]?.name || FIELD_NAMES.ZIP;
    return {
      '{SCHOOL_NAME}': siteMeta.siteName,
      '{SUBJECT}': _subjectLabel || '',
      '{FIRST_NAME}': userValues?.[firstNameField] || '',
      '{LAST_NAME}': userValues?.[lastNameField] || '',
      '{EMAIL}': userValues?.[emailField] || '',
      '{ZIP}': userValues?.[zipCodeField] || '',
      '{PROGRAM_NAME}': userValues?.[bestMatchingProgramField]?.label || '',
      '{PROGRAM_VALUE}': getProgramValue(
        userValues?.[bestMatchingProgramField]?.guid,
        programs
      ),
    };
  }, [
    siteMeta.siteName,
    formValues,
    submittedUserFormValues,
    allQuestionsInForm,
    formStatus.dynamicOptions,
  ]);

  // merge default tokens with any passed in
  const currentTokens = useMemo(
    () => ({ ...defaultTokenMap, ...additionalTokens }),
    [defaultTokenMap, additionalTokens]
  );

  return useMemo(() => {
    if (!textString || typeof textString !== 'string') {
      return textString;
    }
    return tokenReplacement(textString, currentTokens, {
      phoneNumber:
        formValues?.[allQuestionsInForm?.[PRIMARY_PHONE]?.name] ||
        submittedUserFormValues?.[FIELD_NAMES.PRIMARY_PHONE] ||
        '',
      secondaryPhoneNumber:
        formValues?.[FIELD_NAMES.SECONDARY_PHONE] ||
        submittedUserFormValues?.[FIELD_NAMES.SECONDARY_PHONE] ||
        '',
    });
  }, [textString, currentTokens, formValues, submittedUserFormValues]);
}
